<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useUserStore } from '@/stores/user';
import { storeToRefs } from 'pinia';
import { useUiControlStore } from '@/stores/ui-control';
import MobileLogin from '@/components/warning_banners/use_desktop/MobileLogin.vue';
import MobileSignUp from '@/components/warning_banners/use_desktop/MobileSignUp.vue';
import SmallComputerSignUp from '@/components/warning_banners/use_desktop/SmallComputerSignUp.vue';
import SmallComputerLogin from '@/components/warning_banners/use_desktop/SmallComputerLogin.vue';
import { useRouter } from 'vue-router';

const MINIMUM_SUPPORTED_SCREEN_WIDTH = 768;

const uiControlStore = useUiControlStore();
const userStore = useUserStore();
const { displayUseDesktopModal } = storeToRefs(uiControlStore);
const { user } = storeToRefs(userStore);

const router = useRouter();

const isUserAgentForMobile = (userAgent: string) => {
	return /Mobi|Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
};
const isMobile = computed<boolean>(() => {
	return isUserAgentForMobile(navigator.userAgent);
});
const isSmallComputer = ref<boolean>(false);

type LoginStatus = 'NOT_LOGGED_IN' | 'JUST_SIGNED_UP' | 'LOGGED_IN';
const loginStatus = computed<LoginStatus>(() => {
	if (!user.value) {
		return 'NOT_LOGGED_IN';
	} else {
		const signUpQueryParams = user.value.dashboardTemporaryData?.signUpQueryParams;
		if (signUpQueryParams !== undefined && signUpQueryParams !== null) {
			return 'JUST_SIGNED_UP';
		} else {
			return 'LOGGED_IN';
		}
	}
});

const allowedOnCurrentRoute = computed<boolean>(() => {
	return router.currentRoute.value.meta.allowedOnSmallScreens === true;
});

const shouldShowMobileLoginBanner = computed<boolean>(() => {
	return !allowedOnCurrentRoute.value && isMobile.value && loginStatus.value === 'LOGGED_IN';
});

const shouldShowMobileSignUpBanner = computed<boolean>(() => {
	return !allowedOnCurrentRoute.value && isMobile.value && loginStatus.value === 'JUST_SIGNED_UP';
});

const shouldShowSmallComputerLoginBanner = computed<boolean>(() => {
	return !allowedOnCurrentRoute.value && !isMobile.value && isSmallComputer.value && loginStatus.value === 'LOGGED_IN';
});

const shouldShowSmallComputerSignUpBanner = computed<boolean>(() => {
	return (
		!allowedOnCurrentRoute.value && !isMobile.value && isSmallComputer.value && loginStatus.value === 'JUST_SIGNED_UP'
	);
});

watch(
	shouldShowMobileLoginBanner,
	(newValue) => {
		if (newValue) {
			uiControlStore.openUseDesktopModal();
		} else {
			uiControlStore.hideUseDesktopModal();
		}
	},
	{ immediate: true },
);

watch(
	shouldShowMobileSignUpBanner,
	(newValue) => {
		if (newValue) {
			uiControlStore.openUseDesktopModal();
		} else {
			uiControlStore.hideUseDesktopModal();
		}
	},
	{ immediate: true },
);

watch(
	shouldShowSmallComputerLoginBanner,
	(newValue) => {
		if (newValue) {
			uiControlStore.openUseDesktopModal();
		} else {
			uiControlStore.hideUseDesktopModal();
		}
	},
	{ immediate: true },
);

watch(
	shouldShowSmallComputerSignUpBanner,
	(newValue) => {
		if (newValue) {
			uiControlStore.openUseDesktopModal();
		} else {
			uiControlStore.hideUseDesktopModal();
		}
	},
	{ immediate: true },
);

const handleWindowResize = () => {
	isSmallComputer.value = window.innerWidth < MINIMUM_SUPPORTED_SCREEN_WIDTH;
};
onMounted(() => {
	window.addEventListener('resize', handleWindowResize);
	handleWindowResize();
});
onUnmounted(() => {
	window.removeEventListener('resize', handleWindowResize);
});
</script>

<template>
	<MobileSignUp v-if="displayUseDesktopModal && shouldShowMobileSignUpBanner" />
	<MobileLogin v-else-if="displayUseDesktopModal && shouldShowMobileLoginBanner" />
	<SmallComputerSignUp v-else-if="displayUseDesktopModal && shouldShowSmallComputerSignUpBanner" />
	<SmallComputerLogin v-else-if="displayUseDesktopModal && shouldShowSmallComputerLoginBanner" />
</template>

<style scoped></style>
